import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Calendar, DollarSign, TrendingDown, BookOpen, Settings, Menu, X, Waves, Database, Users, MessageSquare, FileText, Bell, LogOut, User, Key, RefreshCw, Download, Upload, PlusCircle } from 'lucide-react';
import { usePropiedad } from '../contexts/PropiedadContext';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import packageJson from '../../package.json';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PRODUCTION_URL = 'https://propertysync-3.emergent.host/api';

const Layout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [modalPasswordOpen, setModalPasswordOpen] = useState(false);
  const [modalSyncOpen, setModalSyncOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [passwordForm, setPasswordForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [changingPassword, setChangingPassword] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { propiedades, propiedadSeleccionada, cambiarPropiedad } = usePropiedad();
  const { usuario, logout, isAdmin, token } = useAuth();

  const handleLogout = () => {
    logout();
    toast.success('Sesión cerrada');
    navigate('/login');
  };

  const handleChangePassword = async () => {
    if (!passwordForm.current_password || !passwordForm.new_password || !passwordForm.confirm_password) {
      toast.error('Por favor completa todos los campos');
      return;
    }

    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('Las contraseñas nuevas no coinciden');
      return;
    }

    if (passwordForm.new_password.length < 6) {
      toast.error('La nueva contraseña debe tener al menos 6 caracteres');
      return;
    }

    try {
      setChangingPassword(true);
      await axios.post(`${API}/auth/change-password`, {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Contraseña actualizada exitosamente');
      setModalPasswordOpen(false);
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cambiar la contraseña');
    } finally {
      setChangingPassword(false);
    }
  };

  // Sync functions
  const handleOpenSyncModal = async () => {
    setModalSyncOpen(true);
    setSyncStatus(null);
    
    // Get current status
    try {
      const response = await axios.get(`${API}/sync-status`);
      setSyncStatus({ preview: response.data });
    } catch (error) {
      console.error('Error getting sync status:', error);
    }
  };

  const handleSyncFromProduction = async () => {
    setSyncing(true);
    try {
      // Step 1: Export from production
      toast.info('Descargando datos de producción...');
      const exportResponse = await axios.get(`${PRODUCTION_URL}/export-data`, {
        timeout: 60000
      });
      
      if (!exportResponse.data || !exportResponse.data.collections) {
        throw new Error('Formato de datos inválido de producción');
      }

      // Step 2: Import to preview (merge)
      toast.info('Importando datos a preview...');
      const blob = new Blob([JSON.stringify(exportResponse.data)], { type: 'application/json' });
      const formData = new FormData();
      formData.append('file', blob, 'production_data.json');

      const importResponse = await axios.post(`${API}/import-data-merge`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000
      });

      toast.success(`✅ Sincronización completada: ${importResponse.data.total_inserted} insertados, ${importResponse.data.total_updated} actualizados`);
      
      // Refresh status
      const statusResponse = await axios.get(`${API}/sync-status`);
      setSyncStatus({ preview: statusResponse.data, lastSync: new Date().toISOString() });
      
      // Reload page to reflect changes
      setTimeout(() => {
        window.location.reload();
      }, 2000);
      
    } catch (error) {
      console.error('Sync error:', error);
      if (error.response?.status === 404) {
        toast.error('El endpoint de sync no existe en producción. Despliega primero esta versión.');
      } else {
        toast.error(error.response?.data?.detail || 'Error al sincronizar datos');
      }
    } finally {
      setSyncing(false);
    }
  };

  const handleExportData = async () => {
    try {
      toast.info('Exportando datos...');
      const response = await axios.get(`${API}/export-data`);
      
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `r3ntapp_backup_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success('Backup descargado exitosamente');
    } catch (error) {
      toast.error('Error al exportar datos');
    }
  };

  const handleSyncToProduction = async () => {
    setSyncing(true);
    try {
      // Step 1: Export from preview
      toast.info('Exportando datos de preview...');
      const exportResponse = await axios.get(`${API}/export-data`, {
        timeout: 60000
      });
      
      if (!exportResponse.data || !exportResponse.data.collections) {
        throw new Error('Formato de datos inválido');
      }

      // Step 2: Import to production (merge)
      toast.info('Enviando datos a producción...');
      const blob = new Blob([JSON.stringify(exportResponse.data)], { type: 'application/json' });
      const formData = new FormData();
      formData.append('file', blob, 'preview_data.json');

      const importResponse = await axios.post(`${PRODUCTION_URL}/import-data-merge`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000
      });

      toast.success(`✅ Datos enviados a producción: ${importResponse.data.total_inserted} insertados, ${importResponse.data.total_updated} actualizados`);
      
      // Refresh status
      const statusResponse = await axios.get(`${API}/sync-status`);
      setSyncStatus({ preview: statusResponse.data, lastSync: new Date().toISOString() });
      
    } catch (error) {
      console.error('Sync to production error:', error);
      if (error.response?.status === 404) {
        toast.error('El endpoint de sync no existe en producción. Despliega primero esta versión.');
      } else {
        toast.error(error.response?.data?.detail || 'Error al enviar datos a producción');
      }
    } finally {
      setSyncing(false);
    }
  };

  const menuItems = [
    { path: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/admin/calendario', label: 'Calendario', icon: Calendar },
    { path: '/admin/reservas', label: 'Reservas', icon: BookOpen },
    { path: '/admin/solicitudes-reserva', label: 'Solicitudes', icon: Bell },
    { path: '/admin/clientes', label: 'Clientes', icon: Users },
    { path: '/admin/mensajes-whatsapp', label: 'Mensajes WhatsApp', icon: MessageSquare },
    { path: '/admin/contratos', label: 'Contratos', icon: FileText },
    { path: '/admin/ingresos', label: 'Ingresos', icon: DollarSign },
    { path: '/admin/gastos', label: 'Gastos', icon: TrendingDown },
    { path: '/admin/datos', label: 'Datos', icon: Database },
    { path: '/admin/configuracion', label: 'Configuración', icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#8FE0FF] via-[#F7E8C9] to-[#8FE0FF]">
      {/* Header */}
      <header className="bg-white/90 backdrop-blur-md shadow-lg sticky top-0 z-50">
        <div className="container mx-auto px-3 sm:px-4">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2 sm:gap-3">
              <button
                data-testid="menu-toggle-btn"
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="lg:hidden p-1.5 sm:p-2 rounded-lg hover:bg-[#8FE0FF]/20 transition-colors"
                aria-label="Abrir menú"
              >
                {sidebarOpen ? <X className="w-6 h-6 text-[#0E3A53]" /> : <Menu className="w-6 h-6 text-[#0E3A53]" />}
              </button>
              <Waves className="w-7 h-7 sm:w-8 sm:h-8 text-[#1AA1D8] shrink-0" />
              <h1 className="text-lg sm:text-2xl font-bold text-[#0E3A53] flex items-center gap-1.5" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                R3NTapp.
                <span className="inline-block text-[11px] sm:text-xs font-medium text-[#1AA1D8] bg-[#1AA1D8]/10 px-1.5 sm:px-2 py-0.5 rounded-full border border-[#1AA1D8]/20 mt-0.5 shrink-0">v{packageJson.version}</span>
              </h1>
            </div>
            
            {/* Selector de Propiedad y Usuario */}
            <div className="flex items-center gap-2 sm:gap-4">
              {/* Selector de Propiedad */}
              <div className="flex items-center gap-1.5 sm:gap-2">
                <span className="hidden md:inline text-sm text-[#0E3A53] font-medium">Propiedad:</span>
                <select
                  value={propiedadSeleccionada?.id_propiedad || ''}
                  onChange={(e) => cambiarPropiedad(e.target.value)}
                  className="max-w-[130px] sm:max-w-[190px] md:max-w-none px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg bg-white text-[#0E3A53] text-xs sm:text-sm font-medium shadow-sm sm:shadow-md border sm:border-2 border-[#8FE0FF] focus:outline-none focus:ring-2 focus:ring-[#1AA1D8] truncate"
                  data-testid="property-selector"
                >
                  {propiedades.map(p => (
                    <option key={p.id_propiedad} value={p.id_propiedad}>{p.nombre}</option>
                  ))}
                </select>
              </div>

              {/* Separador */}
              <div className="hidden md:block w-px h-8 bg-[#8FE0FF]"></div>

              {/* Info de usuario y logout */}
              <div className="flex items-center gap-1 sm:gap-3">
                <div className="hidden md:flex items-center gap-2 text-sm">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-r from-[#1AA1D8] to-[#0E3A53] flex items-center justify-center">
                    <User className="w-4 h-4 text-white" />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-[#0E3A53] font-medium text-xs truncate max-w-[120px]">
                      {usuario?.email}
                    </span>
                    {isAdmin() && (
                      <span className="text-[10px] text-[#1AA1D8] font-semibold">Admin</span>
                    )}
                  </div>
                </div>
                {isAdmin() && (
                  <button
                    onClick={handleOpenSyncModal}
                    className="p-1.5 sm:p-2 rounded-lg hover:bg-[#25D366]/20 transition-colors group"
                    title="Sincronizar datos"
                    data-testid="sync-data-btn"
                  >
                    <RefreshCw className="w-5 h-5 text-[#25D366] group-hover:text-[#25D366]" />
                  </button>
                )}
                <button
                  onClick={() => setModalPasswordOpen(true)}
                  className="p-1.5 sm:p-2 rounded-lg hover:bg-[#1AA1D8]/20 transition-colors group"
                  title="Cambiar contraseña"
                  data-testid="change-password-btn"
                >
                  <Key className="w-5 h-5 text-[#0E3A53] group-hover:text-[#1AA1D8]" />
                </button>
                <button
                  onClick={handleLogout}
                  className="p-1.5 sm:p-2 rounded-lg hover:bg-[#FF7F6A]/20 transition-colors group"
                  title="Cerrar sesión"
                  data-testid="logout-btn"
                >
                  <LogOut className="w-5 h-5 text-[#0E3A53] group-hover:text-[#FF7F6A]" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside
          className={`
            fixed lg:static inset-y-0 left-0 z-40 w-64 bg-white/95 backdrop-blur-md shadow-xl
            transform transition-transform duration-300 lg:translate-x-0
            ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
            mt-16 lg:mt-0 flex flex-col h-[calc(100vh-4rem)] lg:h-screen
          `}
        >
          <nav className="p-4 space-y-2 flex-1 overflow-y-auto">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  onClick={() => setSidebarOpen(false)}
                  className={`
                    flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all
                    ${isActive
                      ? 'bg-gradient-to-r from-[#1AA1D8] to-[#0E3A53] text-white shadow-lg'
                      : 'text-[#0E3A53] hover:bg-[#8FE0FF]/20'
                    }
                  `}
                >
                  <Icon className="w-5 h-5" />
                  {item.label}
                </Link>
              );
            })}
          </nav>

          {/* Versión de la App */}
          <div className="p-4 border-t border-[#8FE0FF]/30 mt-auto text-center">
            <p className="text-xs text-[#0E3A53]/50 font-medium">R3NTapp. v{packageJson.version}</p>
          </div>

          {/* Info usuario en mobile */}
          <div className="md:hidden p-4 border-t border-[#8FE0FF]/30">
            <div className="flex items-center gap-3 px-4 py-3 bg-[#8FE0FF]/10 rounded-xl">
              <div className="w-10 h-10 rounded-full bg-gradient-to-r from-[#1AA1D8] to-[#0E3A53] flex items-center justify-center">
                <User className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#0E3A53] truncate">{usuario?.email}</p>
                {isAdmin() && (
                  <p className="text-xs text-[#1AA1D8] font-semibold">Administrador</p>
                )}
              </div>
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-3 sm:p-4 lg:p-8 pb-24 lg:pb-8 w-full max-w-full overflow-hidden">
          <div className="max-w-7xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Barra de Navegación Inferior Móvil (Bottom Navigation) */}
      <nav className="mobile-bottom-nav lg:hidden flex items-center justify-around px-2 py-1 safe-area-pb">
        <Link
          to="/admin/dashboard"
          className={`mobile-bottom-nav-item ${location.pathname === '/admin/dashboard' ? 'active' : ''}`}
          data-testid="bottom-nav-dashboard"
        >
          <LayoutDashboard className="w-5 h-5 mb-0.5" />
          <span>Inicio</span>
        </Link>

        <Link
          to="/admin/calendario"
          className={`mobile-bottom-nav-item ${location.pathname === '/admin/calendario' ? 'active' : ''}`}
          data-testid="bottom-nav-calendario"
        >
          <Calendar className="w-5 h-5 mb-0.5" />
          <span>Calendario</span>
        </Link>

        {/* Botón Central Destacado: Gasto Rápido */}
        <Link
          to="/gasto-rapido"
          className="flex flex-col items-center justify-center -mt-5 group"
          data-testid="bottom-nav-gasto-rapido"
          title="Nuevo Gasto Rápido"
        >
          <div className="w-12 h-12 rounded-full bg-gradient-to-r from-[#1AA1D8] to-[#0E3A53] text-white flex items-center justify-center shadow-lg border-2 border-white group-active:scale-95 transition-transform">
            <PlusCircle className="w-6 h-6" />
          </div>
          <span className="text-[11px] font-bold text-[#0E3A53] mt-0.5">+ Gasto</span>
        </Link>

        <Link
          to="/admin/gastos"
          className={`mobile-bottom-nav-item ${location.pathname === '/admin/gastos' ? 'active' : ''}`}
          data-testid="bottom-nav-gastos"
        >
          <TrendingDown className="w-5 h-5 mb-0.5" />
          <span>Gastos</span>
        </Link>

        <button
          type="button"
          onClick={() => setSidebarOpen(true)}
          className="mobile-bottom-nav-item"
          data-testid="bottom-nav-menu"
        >
          <Menu className="w-5 h-5 mb-0.5" />
          <span>Menú</span>
        </button>
      </nav>

      {/* Overlay para mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Modal de cambio de contraseña */}
      <Dialog open={modalPasswordOpen} onOpenChange={setModalPasswordOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#0E3A53] flex items-center gap-2">
              <Key className="w-5 h-5 text-[#1AA1D8]" />
              Cambiar Contraseña
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="current-password" className="text-[#0E3A53]">Contraseña Actual</Label>
              <Input
                id="current-password"
                type="password"
                value={passwordForm.current_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                placeholder="Ingresa tu contraseña actual"
                data-testid="input-current-password"
              />
            </div>
            <div>
              <Label htmlFor="new-password" className="text-[#0E3A53]">Nueva Contraseña</Label>
              <Input
                id="new-password"
                type="password"
                value={passwordForm.new_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                placeholder="Mínimo 6 caracteres"
                data-testid="input-new-password"
              />
            </div>
            <div>
              <Label htmlFor="confirm-password" className="text-[#0E3A53]">Confirmar Nueva Contraseña</Label>
              <Input
                id="confirm-password"
                type="password"
                value={passwordForm.confirm_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                placeholder="Repite la nueva contraseña"
                data-testid="input-confirm-password"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button 
              variant="outline" 
              onClick={() => {
                setModalPasswordOpen(false);
                setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
              }}
              disabled={changingPassword}
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleChangePassword}
              disabled={changingPassword}
              className="bg-gradient-to-r from-[#1AA1D8] to-[#0E3A53]"
              data-testid="save-password-btn"
            >
              {changingPassword ? 'Guardando...' : 'Cambiar Contraseña'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de sincronización de datos */}
      <Dialog open={modalSyncOpen} onOpenChange={setModalSyncOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#0E3A53] flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-[#25D366]" />
              Sincronizar Datos
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-4 bg-[#F7E8C9]/30 rounded-lg">
              <p className="text-sm text-[#0E3A53]/70 mb-2">
                <strong>Producción:</strong> propertysync-3.emergent.host
              </p>
              <p className="text-sm text-[#0E3A53]/70">
                <strong>Preview:</strong> {BACKEND_URL?.replace('https://', '').replace('/api', '')}
              </p>
            </div>

            {syncStatus?.preview && (
              <div className="p-4 bg-[#8FE0FF]/20 rounded-lg">
                <p className="text-sm font-semibold text-[#0E3A53] mb-2">Estado actual (Preview):</p>
                <p className="text-sm text-[#0E3A53]/70">
                  Total documentos: <strong>{syncStatus.preview.total_documents}</strong>
                </p>
                {syncStatus.lastSync && (
                  <p className="text-xs text-[#25D366] mt-1">
                    Última sincronización: {new Date(syncStatus.lastSync).toLocaleString()}
                  </p>
                )}
              </div>
            )}

            <div className="space-y-3">
              <Button
                onClick={handleSyncFromProduction}
                disabled={syncing}
                className="w-full bg-gradient-to-r from-[#25D366] to-[#128C7E] hover:shadow-lg"
                data-testid="sync-from-production-btn"
              >
                {syncing ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Sincronizando...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Traer datos de Producción → Preview
                  </>
                )}
              </Button>

              <Button
                onClick={handleSyncToProduction}
                disabled={syncing}
                className="w-full bg-gradient-to-r from-[#1AA1D8] to-[#0E3A53] hover:shadow-lg"
                data-testid="sync-to-production-btn"
              >
                {syncing ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Enviar datos de Preview → Producción
                  </>
                )}
              </Button>

              <hr className="border-[#8FE0FF]/30" />

              <Button
                onClick={handleExportData}
                variant="outline"
                className="w-full border-[#1AA1D8] text-[#1AA1D8] hover:bg-[#1AA1D8]/10"
                data-testid="export-data-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Descargar Backup (JSON)
              </Button>
            </div>

            <p className="text-xs text-[#0E3A53]/50 text-center">
              La sincronización fusiona los datos sin eliminar registros existentes.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Layout;
